import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  CircularProgressProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { useContext, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  getLaborHour,
  getServiceHour
} from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { useGetEmployeeRolesQuery } from '../../../redux/api'
import { ServiceInfoStatusEnum } from '../../../sdk'
import { formatCurrency, formatPercentage } from '../../../utils'
import { CenteredLoading } from '../../CenteredLoading/CenteredLoading'
function CircularProgressWithLabel(
  props: CircularProgressProps & { value: number }
) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Typography
          variant="caption"
          component="div"
          color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  )
}
interface TechWithHours {
  tech: string
  hours: number
}

interface TechWithCost extends TechWithHours {
  hourlyRate: number
}

export const ProfitDialog = ({
  setOpen
}: {
  setOpen: (s: boolean) => void
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [view, setView] = useState<'table' | 'grid'>('table')
  const { repairOrder, services } = useContext(RepairOrderContext)
  const { data: employeeRoles, isFetching } = useGetEmployeeRolesQuery({
    authorization: accessToken,
    shop: shopId
  })
  const repairOrderPricing = useMemo(
    () =>
      repairOrder?.entry!.info!.isEstimate
        ? repairOrder?.totalPricing!
        : repairOrder?.authorizedPricing!,
    [repairOrder]
  )
  const laborPricing = useMemo(
    () => repairOrderPricing?.laborRetailPrice || 0,
    [repairOrderPricing]
  )
  const partsPricing = useMemo(
    () => repairOrderPricing?.partsRetailPrice || 0,
    [repairOrderPricing]
  )
  const totalPricing = useMemo(
    () => repairOrderPricing?.total || 0,
    [repairOrderPricing]
  )
  const techWithHours = useMemo(() => {
    if (!services || !employeeRoles) {
      return
    }
    const filteredServices = services.filter(
      (s) =>
        s.entry!.info!.status === ServiceInfoStatusEnum.Authorized ||
        s.entry!.info!.status === ServiceInfoStatusEnum.Recommended
    )
    const temp = [] as TechWithHours[]
    for (const service of filteredServices) {
      if (service.laborOverrides!.length === 0) {
        continue
      }
      const hasServiceTech =
        Object.values(service.serviceTechFractionalCredit || {}).length !== 0
      const hasLaborTech =
        Object.keys(service.laborOverrideTechFractionalCredit || {}).length !==
          0 &&
        service.laborOverrides!.every(
          (labor) => labor.id! in service.laborOverrideTechFractionalCredit!
        )
      if (!hasServiceTech && !hasLaborTech) {
        return
      }
      if (hasServiceTech) {
        const totalHours = getServiceHour(service)
        Object.keys(service.serviceTechFractionalCredit || {}).forEach(
          (tech) => {
            const credit = service.serviceTechFractionalCredit![tech]
            temp.push({
              tech,
              hours: credit * totalHours
            })
          }
        )
      } else if (hasLaborTech) {
        for (const laborOverride of service.laborOverrides!) {
          const totalHours = getLaborHour(
            laborOverride,
            service.motorApplications,
            laborOverride.laborMatrixCatalog === undefined
              ? undefined
              : service.catalogLaborMatrixEntries![
                  laborOverride.laborMatrixCatalog
                ]
          )
          Object.keys(
            service.laborOverrideTechFractionalCredit![laborOverride.id!]
          ).forEach((tech) => {
            const credit =
              service.laborOverrideTechFractionalCredit![laborOverride.id!][
                tech
              ]
            temp.push({
              tech,
              hours: credit * totalHours
            })
          })
        }
      }
    }
    return temp
  }, [employeeRoles, services])
  const techWithCost = useMemo(() => {
    if (!techWithHours || !employeeRoles) {
      return
    }
    const temp = [] as TechWithCost[]
    for (const tech of techWithHours) {
      if (tech.hours === 0) {
        temp.push({ ...tech, hourlyRate: 0 })
        continue
      }
      const employee = employeeRoles.find(
        (e) => e.userProfile!.id! === tech.tech
      )
      if (!employee || employee.hourlyRate === undefined) {
        return
      }
      temp.push({
        ...tech,
        hourlyRate: employee.hourlyRate
      })
    }
    return temp
  }, [employeeRoles, techWithHours])
  const laborCost = useMemo(
    () =>
      techWithCost?.reduce((acc, cur) => acc + cur.hours * cur.hourlyRate, 0),
    [techWithCost]
  )
  const partsCost = useMemo(
    () => repairOrderPricing?.partsCost || 0,
    [repairOrderPricing]
  )
  const totalCost = useMemo(
    () =>
      laborCost !== undefined && partsCost !== undefined
        ? laborCost + partsCost
        : undefined,
    [laborCost, partsCost]
  )
  const laborProfit = useMemo(
    () =>
      laborCost !== undefined && laborPricing !== undefined
        ? laborPricing - laborCost
        : undefined,
    [laborCost, laborPricing]
  )
  const partsProfit = useMemo(
    () =>
      partsCost !== undefined && partsPricing !== undefined
        ? partsPricing - partsCost
        : undefined,
    [partsCost, partsPricing]
  )
  const totalProfit = useMemo(
    () =>
      totalCost !== undefined && totalPricing !== undefined
        ? totalPricing - totalCost
        : undefined,
    [totalCost, totalPricing]
  )
  const laborMargin = useMemo(
    () => (laborProfit !== undefined ? laborProfit / laborPricing : undefined),
    [laborPricing, laborProfit]
  )
  const partsMargin = useMemo(
    () => (partsProfit !== undefined ? partsProfit / partsPricing : undefined),
    [partsPricing, partsProfit]
  )
  const totalMargin = useMemo(
    () => (totalProfit !== undefined ? totalProfit / totalPricing : undefined),
    [totalPricing, totalProfit]
  )
  const headers = useMemo(() => ['', 'Total', 'Parts', 'Labor'], [])
  const rows = useMemo(
    () => [
      [
        'Cost',
        totalCost === undefined ? 'N/A' : formatCurrency(totalCost, true),
        partsCost === undefined ? 'N/A' : formatCurrency(partsCost, true),
        laborCost === undefined ? 'N/A' : formatCurrency(laborCost, true)
      ],
      [
        'Price',
        totalPricing === undefined ? 'N/A' : formatCurrency(totalPricing, true),
        partsPricing === undefined ? 'N/A' : formatCurrency(partsPricing, true),
        laborPricing === undefined ? 'N/A' : formatCurrency(laborPricing, true)
      ],
      [
        'Profit',
        totalProfit === undefined ? 'N/A' : formatCurrency(totalProfit, true),
        partsProfit === undefined ? 'N/A' : formatCurrency(partsProfit, true),
        laborProfit === undefined ? 'N/A' : formatCurrency(laborProfit, true)
      ],
      [
        'Margin',
        totalMargin === undefined ? 'N/A' : formatPercentage(totalMargin),
        partsMargin === undefined ? 'N/A' : formatPercentage(partsMargin),
        laborMargin === undefined ? 'N/A' : formatPercentage(laborMargin)
      ]
    ],
    [
      laborCost,
      laborMargin,
      laborPricing,
      laborProfit,
      partsCost,
      partsMargin,
      partsPricing,
      partsProfit,
      totalCost,
      totalMargin,
      totalPricing,
      totalProfit
    ]
  )

  return (
    <Dialog open={true} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Profit Summary</DialogTitle>
      <DialogContent>
        {isFetching && <CenteredLoading />}
        <ToggleButtonGroup
          sx={{ mb: 2 }}
          value={view}
          exclusive
          onChange={(
            event: React.MouseEvent<HTMLElement>,
            nextView: string
          ) => {
            if (nextView) {
              setView(nextView as 'table' | 'grid')
            }
          }}>
          <ToggleButton value="table" aria-label="table">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value="grid" aria-label="grid">
            <ViewModuleIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        {!techWithHours && (
          <FormHelperText error>
            There are services with no technicians assigned. Please assign a
            technician to get labor cost estimates.
          </FormHelperText>
        )}
        {techWithHours && !techWithCost && (
          <FormHelperText error>
            There are technicians with no hourly rate set. Please set hourly
            rate in shop settings to get labor cost estimates.
          </FormHelperText>
        )}
        {view === 'table' && (
          <TableContainer component={Paper}>
            <Table sx={{ width: '100%' }}>
              <TableHead sx={{ bgcolor: 'primary.light' }}>
                <TableRow>
                  {headers.map((header, i) =>
                    i === 0 ? (
                      <TableCell key={i}></TableCell>
                    ) : (
                      <TableCell align="right" key={i}>
                        {header}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}>
                    {row.map((cell, j) =>
                      j === 0 ? (
                        <TableCell
                          component="th"
                          scope="row"
                          key={j}
                          sx={{
                            bgcolor: 'primary.light'
                          }}>
                          {cell}
                        </TableCell>
                      ) : (
                        <TableCell align="right" key={j}>
                          {cell}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {view === 'grid' && (
          <Grid container spacing={1}>
            {headers.map((header, i) =>
              i === 0 ? (
                <Grid key={i} xs={1.5} item></Grid>
              ) : (
                <Grid key={i} xs={3.5} item>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'primary.light',
                      p: 1,
                      width: '100%',
                      display: 'flex',
                      boxSizing: 'border-box',
                      justifyContent: 'center'
                    }}>
                    <Typography variant="subtitle1">{header}</Typography>
                  </Card>
                </Grid>
              )
            )}
            {rows.map((row, i) =>
              row.map((cell, j) =>
                j === 0 ? (
                  <Grid key={j} xs={1.5} item>
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: 'primary.light',
                        p: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        boxSizing: 'border-box',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ writingMode: 'vertical-rl' }}>
                        {' '}
                        {cell}
                      </Typography>
                    </Card>
                  </Grid>
                ) : i === 3 && cell !== 'N/A' ? (
                  <Grid key={j} xs={3.5} item>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1,
                        width: '100%',
                        height: '80px',
                        display: 'flex',
                        boxSizing: 'border-box',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                      <CircularProgressWithLabel
                        value={Number(cell.slice(0, -1))}
                        variant="determinate"
                      />
                    </Card>
                  </Grid>
                ) : (
                  <Grid key={j} xs={3.5} item>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1,
                        width: '100%',
                        height: '80px',
                        display: 'flex',
                        boxSizing: 'border-box',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                      <Typography>{cell}</Typography>
                    </Card>
                  </Grid>
                )
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
